import React, { useEffect, useState } from "react";
import { Button, Card, message, Popconfirm, Tooltip } from "antd";
import { CheckCircleOutlined, LinkOutlined } from "@ant-design/icons";
import AuthService from "../../services/auth";
import { resolvedBaseURL } from "../../core/instance";
import { trackOAuthEvent } from "../../utils/analytics";

interface LinkedAccount {
  id: string;
  email: string;
  linkedAt: string;
}

interface OAuthAccountsData {
  linkedAccounts: {
    google?: LinkedAccount;
    apple?: LinkedAccount;
  };
  hasGoogle: boolean;
  hasApple: boolean;
}

// Linking a new provider is disabled: the real backend endpoint
// (POST /auth/oauth/link) needs a Google/Apple token obtained client-side,
// but this app has no Google/Apple sign-in SDK integrated - the redirect
// flow below is a dead end (confirmed the backend's GET /auth/google and
// GET /auth/apple routes silently ignore `?link=true`; there is no session
// or token mechanism that carries "link to this logged-in user" through a
// redirect round-trip). Re-enabling this requires adding a real client-side
// OAuth SDK (Google Identity Services, Apple Sign-In JS) with a registered
// Web Client ID/Services ID, then switching these handlers to obtain a
// provider token and POST it to AuthService.linkOAuth instead of
// redirecting. Unlinking an already-connected account is unaffected and
// stays enabled.
const ACCOUNT_LINKING_ENABLED = false;

export default function OAuthAccounts() {
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<{
    google: boolean;
    apple: boolean;
  }>({ google: false, apple: false });
  const [accounts, setAccounts] = useState<OAuthAccountsData | null>(null);

  const fetchLinkedAccounts = async () => {
    try {
      setLoading(true);
      const data = await AuthService.getLinkedAccounts();
      setAccounts(data);
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to load linked accounts",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinkedAccounts();
  }, []);

  const handleLinkGoogle = () => {
    setOauthLoading({ ...oauthLoading, google: true });
    trackOAuthEvent("google", "link_attempt");
    // Not functional yet - see ACCOUNT_LINKING_ENABLED comment above. Kept
    // pointed at the real configured backend (not localhost) so this fails
    // safely against whichever environment this build is deployed to,
    // rather than always trying a developer's own machine, if this is ever
    // called before the client-side OAuth SDK work above is done.
    window.location.href = `${resolvedBaseURL}/auth/google?link=true`;
  };

  const handleLinkApple = () => {
    setOauthLoading({ ...oauthLoading, apple: true });
    trackOAuthEvent("apple", "link_attempt");
    window.location.href = `${resolvedBaseURL}/auth/apple?link=true`;
  };

  const handleUnlink = async (provider: "google" | "apple") => {
    try {
      setLoading(true);
      await AuthService.unlinkOAuth({ provider });
      message.success(`${provider} account unlinked successfully`);
      await fetchLinkedAccounts();
      trackOAuthEvent(provider, "unlink", true);
    } catch (error: any) {
      message.error(
        error.response?.data?.message || `Failed to unlink ${provider} account`,
      );
      trackOAuthEvent(provider, "unlink", false);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !accounts) {
    return <div className="p-4">Loading linked accounts...</div>;
  }

  return (
    <div className="bg-white w-full h-full rounded-lg border-gray-200 py-4 md:px-4 px-2">
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Connected Accounts</h3>
        <p className="text-sm text-gray-600">
          Link your social accounts to sign in faster and manage your account
          securely.
        </p>
      </div>

      <div className="space-y-4">
        {/* Google Account */}
        <Card className="shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center">
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              </div>
              <div>
                <p className="font-medium">Google</p>
                {accounts?.hasGoogle && accounts.linkedAccounts.google ? (
                  <p className="text-sm text-gray-500">
                    {accounts.linkedAccounts.google.email}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">Not connected</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {accounts?.hasGoogle ? (
                <>
                  <CheckCircleOutlined className="text-green-500 text-lg" />
                  <Popconfirm
                    title="Unlink Google Account"
                    description="Are you sure you want to unlink this Google account?"
                    onConfirm={() => handleUnlink("google")}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" danger>
                      Unlink
                    </Button>
                  </Popconfirm>
                </>
              ) : ACCOUNT_LINKING_ENABLED ? (
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  onClick={handleLinkGoogle}
                  loading={oauthLoading.google}
                >
                  Link Account
                </Button>
              ) : (
                <Tooltip title="We're rebuilding account linking - check back soon!">
                  <Button type="primary" icon={<LinkOutlined />} disabled>
                    Coming Soon
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        </Card>

        {/* Apple Account */}
        <Card className="shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
              </div>
              <div>
                <p className="font-medium">Apple</p>
                {accounts?.hasApple && accounts.linkedAccounts.apple ? (
                  <p className="text-sm text-gray-500">
                    {accounts.linkedAccounts.apple.email}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">Not connected</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {accounts?.hasApple ? (
                <>
                  <CheckCircleOutlined className="text-green-500 text-lg" />
                  <Popconfirm
                    title="Unlink Apple Account"
                    description="Are you sure you want to unlink this Apple account?"
                    onConfirm={() => handleUnlink("apple")}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" danger>
                      Unlink
                    </Button>
                  </Popconfirm>
                </>
              ) : ACCOUNT_LINKING_ENABLED ? (
                <Button
                  type="default"
                  icon={<LinkOutlined />}
                  onClick={handleLinkApple}
                  loading={oauthLoading.apple}
                  className="border-black"
                >
                  Link Account
                </Button>
              ) : (
                <Tooltip title="We're rebuilding account linking - check back soon!">
                  <Button type="default" icon={<LinkOutlined />} disabled>
                    Coming Soon
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> You need at least one authentication method
          (password or OAuth) to keep your account secure. You cannot unlink
          your last remaining authentication method.
        </p>
      </div>
    </div>
  );
}
